import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Zap, Globe, BarChart3, Clock, Headphones } from 'lucide-react';

export const Overview: React.FC = () => {
  const features = [
    {
      title: 'Authentication',
      description: 'Secure API key-based authentication with JWT tokens',
      href: '/authentication',
      accent: '#581689',
      bg: 'bg-purple-50',
      border: 'border-purple-100',
    },
    {
      title: 'Collections',
      description: 'Virtual accounts for seamless payment collection',
      href: '/collections',
      accent: '#0ea5e9',
      bg: 'bg-sky-50',
      border: 'border-sky-100',
    },
    {
      title: 'Payouts',
      description: "Wide-reaching support across Nigeria's financial landscape",
      href: '/payouts',
      accent: '#10b981',
      bg: 'bg-emerald-50',
      border: 'border-emerald-100',
    },
    {
      title: 'Account Management',
      description: 'Monitor balances and manage multiple accounts',
      href: '/account',
      accent: '#f59e0b',
      bg: 'bg-amber-50',
      border: 'border-amber-100',
    },
    {
      title: 'Bill Payment',
      description: 'Pay utility bills, airtime, data and more from your agent account',
      href: '/bill-payment',
      accent: '#ef4444',
      bg: 'bg-red-50',
      border: 'border-red-100',
    },
    {
      title: 'Customer (Crypto)',
      description: 'Onboard customers and accept crypto payments via virtual accounts',
      href: '/customer-crypto',
      accent: '#8b5cf6',
      bg: 'bg-violet-50',
      border: 'border-violet-100',
    },
  ];

  const whyItems = [
    { icon: ShieldCheck, title: 'Secure & Compliant', desc: 'Bank-grade security with full CBN regulatory compliance', color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { icon: Zap, title: 'Developer Friendly', desc: 'RESTful APIs with comprehensive documentation and code examples', color: 'text-amber-600', bg: 'bg-amber-50' },
    { icon: Clock, title: 'Real-time Processing', desc: 'Instant transfers and real-time transaction status updates', color: 'text-sky-600', bg: 'bg-sky-50' },
    { icon: Globe, title: 'Comprehensive Coverage', desc: 'Connectivity with 570+ Nigerian banks and financial providers', color: 'text-purple-600', bg: 'bg-purple-50' },
    { icon: BarChart3, title: 'Scalable Infrastructure', desc: 'Built to handle high-volume transactions without limits', color: 'text-rose-600', bg: 'bg-rose-50' },
    { icon: Headphones, title: '24/7 Support', desc: 'Dedicated technical support team available around the clock', color: 'text-indigo-600', bg: 'bg-indigo-50' },
  ];

  return (
    <div className="max-w-5xl mx-auto">

      {/* Hero */}
      <div className="text-center mb-16 pt-4">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-100 text-xs font-semibold text-[#581689] mb-6">
          <span className="h-1.5 w-1.5 rounded-full bg-[#581689] animate-pulse" />
          API Documentation
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-5 leading-tight tracking-tight">
          Build with the<br />
          <span style={{ color: '#581689' }}>Aku API</span>
        </h1>
        <p className="text-lg text-gray-500 mb-8 max-w-2xl mx-auto leading-relaxed">
          Complete financial infrastructure for Nigerian businesses.
          Collect payments, send money, and manage accounts with powerful, developer-friendly APIs.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <Link
            to="/getting-started"
            className="inline-flex items-center justify-center px-6 py-3 bg-[#581689] text-white font-semibold rounded-xl hover:bg-[#4a1275] shadow-lg shadow-purple-200 hover:shadow-purple-300 transition-all duration-200 text-sm"
          >
            Get Started
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
          <Link
            to="/authentication"
            className="inline-flex items-center justify-center px-6 py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 text-sm"
          >
            View API Reference
          </Link>
        </div>
      </div>

      {/* Feature grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
        {features.map((feature, index) => (
          <Link key={index} to={feature.href} className="group block">
            <div className={`h-full p-5 rounded-2xl border ${feature.bg} ${feature.border} hover:shadow-md transition-all duration-200 group-hover:-translate-y-0.5`}>
              <div
                className="h-1.5 w-8 rounded-full mb-4"
                style={{ backgroundColor: feature.accent }}
              />
              <h3 className="font-semibold text-gray-900 mb-1.5">{feature.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-4">{feature.description}</p>
              <div className="flex items-center text-xs font-semibold transition-colors duration-200" style={{ color: feature.accent }}>
                Explore docs
                <ArrowRight className="ml-1.5 h-3.5 w-3.5 group-hover:translate-x-1 transition-transform duration-200" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Why Aku */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8 mb-12 shadow-sm">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Why Choose Aku API?</h2>
          <p className="text-gray-500 text-sm">Everything you need to build financial products for Nigeria</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {whyItems.map((item, index) => (
            <div key={index} className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${item.bg} shrink-0`}>
                <item.icon className={`h-4 w-4 ${item.color}`} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-0.5">{item.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="rounded-2xl p-10 text-center" style={{ background: 'linear-gradient(135deg, #581689 0%, #7c22b8 100%)' }}>
        <h2 className="text-2xl font-bold text-white mb-3">Ready to Get Started?</h2>
        <p className="text-purple-200 mb-7 max-w-md mx-auto text-sm leading-relaxed">
          Integrate the Aku API into your application in minutes and start processing payments today.
        </p>
        <Link
          to="/getting-started"
          className="inline-flex items-center px-6 py-3 bg-white text-[#581689] font-semibold rounded-xl hover:bg-gray-50 shadow-lg transition-all duration-200 text-sm"
        >
          View Quick Start Guide
          <ArrowRight className="ml-2 h-4 w-4" />
        </Link>
      </div>
    </div>
  );
};
